import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useSEO, generateServiceSchema, generateBreadcrumbSchema } from '../../../utils/seo';

export default function AmbulatoryTransport() {
  const serviceSchema = generateServiceSchema(
    'Ambulatory Medical Transportation',
    'Safe and comfortable ambulatory medical transportation services for patients who can walk with minimal assistance. Professional drivers ensure timely arrival to medical appointments throughout Orange County.'
  );
  
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Services', url: '/#services' },
    { name: 'Ambulatory Transport', url: '/services/ambulatory' }
  ]);

  const combinedSchema = {
    '@context': 'https://schema.org',
    '@graph': [serviceSchema, breadcrumbSchema]
  };

  useSEO({
    title: 'Ambulatory Medical Transportation Westminster CA | InteCare NEMT Services',
    description: 'Professional ambulatory medical transportation services in Westminster CA and Orange County. Safe, comfortable transport for patients who can walk with assistance. Book your ride today.',
    keywords: 'ambulatory transport, ambulatory medical transportation, walking assistance transport, medical appointment transport, Westminster CA ambulatory service',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services/ambulatory`,
    schema: combinedSchema
  });

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/2523a4830b93d6dd2cf3328a80ff2d28.jpg')`
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Ambulatory Transportation Services
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-200">
              Safe, comfortable transportation for patients who can walk but need assistance getting to medical appointments
            </p>
            <div className="flex justify-center">
              <a 
                href="tel:+17147987318"
                className="inline-flex items-center bg-white text-blue-600 hover:bg-gray-100 font-semibold text-lg px-8 py-4 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                Call (714) 798-7318
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What is Ambulatory Transport */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                What is Ambulatory Transportation?
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Ambulatory medical transportation is designed for patients who can walk independently or with minimal assistance but may need help getting to and from their medical appointments safely.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                This service is perfect for seniors, patients recovering from surgery, those with mobility challenges, or anyone who doesn't feel comfortable driving themselves to medical appointments.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Door-to-door service with personal assistance</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Comfortable, climate-controlled vehicles</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Professional, caring drivers</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-green-600 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700">Flexible scheduling to meet your needs</span>
                </div>
              </div>
            </div>
            <div>
              <img 
                src="https://static.readdy.ai/image/d32fda172531ebc64455c87992c4a988/5c1af145e05edb1565b22383bb2a05c9.png"
                alt="Ambulatory Transportation Service"
                className="w-full rounded-lg shadow-lg object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Who Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Who Benefits from Ambulatory Transport?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our ambulatory transportation services are ideal for various patients and situations
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-user-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Senior Citizens</h3>
              <p className="text-gray-600">
                Elderly patients who may not feel comfortable driving or need assistance navigating medical facilities.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-hospital-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Post-Surgery Patients</h3>
              <p className="text-gray-600">
                Patients recovering from minor surgeries who can walk but shouldn't drive during recovery.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-eye-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Vision Impaired</h3>
              <p className="text-gray-600">
                Patients with vision challenges who need safe transportation to eye appointments and treatments.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-pulse-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Chronic Conditions</h3>
              <p className="text-gray-600">
                Patients with conditions like diabetes, heart disease, or arthritis requiring regular medical visits.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-capsule-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Medication Effects</h3>
              <p className="text-gray-600">
                Patients whose medications may impair their ability to drive safely to appointments.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-group-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Family Caregivers</h3>
              <p className="text-gray-600">
                When family members can't provide transportation due to work or other commitments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Features */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Ambulatory Transport Features
            </h2>
            <p className="text-xl text-gray-600">
              Comprehensive care and comfort for every journey
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-door-open-line text-blue-600 text-xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Door-to-Door Service</h3>
                <p className="text-gray-600">Our drivers assist you from your home to the medical facility and back, ensuring safe transitions.</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-hand-heart-line text-green-600 text-xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Personal Assistance</h3>
                <p className="text-gray-600">Help with walking, carrying personal items, and navigating medical facilities when needed.</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-time-line text-purple-600 text-xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Flexible Scheduling</h3>
                <p className="text-gray-600">Book one-time trips or recurring appointments that fit your medical schedule.</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-car-line text-orange-600 text-xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Comfortable Vehicles</h3>
                <p className="text-gray-600">Clean, well-maintained vehicles with easy entry/exit and climate control for your comfort.</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-red-600 text-xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Safety First</h3>
                <p className="text-gray-600">Professional drivers trained in patient care with background checks and safety certifications.</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-money-dollar-circle-line text-teal-600 text-xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Insurance Billing</h3>
                <p className="text-gray-600">We work with most insurance plans and provide assistance with billing and documentation.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Schedule Your Ambulatory Transport?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Experience safe, comfortable, and reliable transportation to your medical appointments with InteCare's professional ambulatory transport services.
          </p>
          <div className="flex justify-center">
            <a 
              href="tel:+17147987318"
              className="inline-flex items-center bg-white text-blue-600 hover:bg-gray-100 font-semibold text-lg px-8 py-4 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (714) 798-7318
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}